import { useState, useEffect, useCallback } from 'react';

// DKG node URLs - try multiple nodes for resilience
const DKG_NODE_URLS = [
  process.env.REACT_APP_DKG_NODE_1 || 'http://localhost:8081',
  process.env.REACT_APP_DKG_NODE_2 || 'http://localhost:8082',
  process.env.REACT_APP_DKG_NODE_3 || 'http://localhost:8083',
  process.env.REACT_APP_DKG_NODE_4 || 'http://localhost:8084',
  process.env.REACT_APP_DKG_NODE_5 || 'http://localhost:8085',
];

/**
 * Hook to fetch and manage token mappings from the DKG nodes.
 *
 * Backend response format (nested):
 *   { "ETH": { "sepolia:polygon": { target_token, is_active, ... } } }
 * Keys are compound: "sourceChain:targetChain"
 *
 * Same-chain + same-token is always auto-allowed by the backend
 * (no DB mapping needed), so getValidWithdrawalTargets includes it automatically.
 */
const useTokenMappings = () => {
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      setError(null);

      for (const nodeUrl of DKG_NODE_URLS) {
        try {
          const response = await fetch(`${nodeUrl}/api/token-mappings`);
          if (!response.ok) continue;

          const data = await response.json();
          if (data.success) {
            setMappings(data.mappings || {});
            setError(null);
            return;
          }
        } catch (nodeErr) {
          console.warn(`Token mappings: node ${nodeUrl} failed:`, nodeErr.message);
          continue;
        }
      }

      setError('Failed to fetch token mappings from any DKG node');
    } catch (err) {
      console.error('Error fetching token mappings:', err);
      setError('Failed to fetch token mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMappings();
  }, []);

  /**
   * Get the target token for a specific source→target route.
   * @param {string} sourceToken - e.g. 'ETH'
   * @param {string} sourceChain - e.g. 'sepolia'
   * @param {string} targetChain - e.g. 'polygon'
   * @returns {string|null}
   */
  const getTargetToken = useCallback((sourceToken, sourceChain, targetChain) => {
    // Same chain + same token is always auto-allowed
    if (sourceChain === targetChain) return sourceToken;

    const tokenMappings = mappings[sourceToken];
    if (!tokenMappings) return null;

    const key = `${sourceChain}:${targetChain}`;
    const mapping = tokenMappings[key];
    return mapping?.target_token || null;
  }, [mappings]);

  /**
   * Get all valid withdrawal targets for a deposit.
   * Includes same-chain same-token (auto-allowed) + all DB mappings.
   *
   * @param {string} sourceToken - Deposited token symbol (e.g. 'ETH')
   * @param {string} sourceChain - Chain where deposit was made (e.g. 'sepolia')
   * @returns {Array<{chain: string, token: string}>}
   */
  const getValidWithdrawalTargets = useCallback((sourceToken, sourceChain) => {
    if (!sourceToken || !sourceChain) return [];

    const targets = [];

    // 1. Same-chain same-token is always auto-allowed by the backend
    targets.push({ chain: sourceChain, token: sourceToken });

    // 2. Add explicit DB mappings for this source token + chain
    const tokenMappings = mappings[sourceToken] || {};
    for (const [key, mapping] of Object.entries(tokenMappings)) {
      const parts = key.split(':');
      if (parts.length !== 2) continue;
      const [srcChain, tgtChain] = parts;
      if (srcChain === sourceChain && mapping.is_active !== false) {
        // Avoid duplicating the same-chain entry
        if (tgtChain !== sourceChain || mapping.target_token !== sourceToken) {
          targets.push({ chain: tgtChain, token: mapping.target_token });
        }
      }
    }

    return targets;
  }, [mappings]);

  /**
   * Get all target chains available for a source token from a specific chain.
   * @param {string} sourceToken
   * @param {string} sourceChain
   * @returns {Array<string>}
   */
  const getAvailableChains = useCallback((sourceToken, sourceChain) => {
    const targets = getValidWithdrawalTargets(sourceToken, sourceChain);
    return [...new Set(targets.map(t => t.chain))];
  }, [getValidWithdrawalTargets]);

  return {
    mappings,
    loading,
    error,
    refetch: fetchMappings,
    getTargetToken,
    getValidWithdrawalTargets,
    getAvailableChains,
  };
};

export default useTokenMappings;
